// Internationalization (i18n) module
// Language is determined by URL parameter (?lang=xx), default is English

import en from './en.json'
import pl from './pl.json'

type Translations = Record<string, string>

const translations: Record<string, Translations> = {
  en,
  pl
}

const SUPPORTED_LOCALES = ['en', 'pl'] as const
type SupportedLocale = typeof SUPPORTED_LOCALES[number]

let currentLocale: SupportedLocale = 'en'

/**
 * Initialize locale from URL parameter (?lang=xx)
 * Falls back to English if not specified or invalid
 */
export function initLocale(): void {
  const params = new URLSearchParams(window.location.search)
  const lang = params.get('lang')?.toLowerCase()

  if (lang && SUPPORTED_LOCALES.includes(lang as SupportedLocale)) {
    currentLocale = lang as SupportedLocale
  } else {
    currentLocale = 'en'
  }

  // Update document lang attribute
  document.documentElement.lang = currentLocale
}

/**
 * Get current locale code
 */
export function getCurrentLocale(): SupportedLocale {
  return currentLocale
}

/**
 * Translate a key to the current locale
 * Returns the key itself if translation is missing (with console warning in dev)
 */
export function t(key: string): string {
  const localeTranslations = translations[currentLocale]
  const translation = localeTranslations?.[key]

  if (translation === undefined) {
    if (import.meta.env.DEV) {
      console.warn(`[i18n] Missing translation for key: "${key}" in locale: "${currentLocale}"`)
    }
    return key
  }

  return translation
}

/**
 * Translate all elements with data-i18n attribute
 * The attribute value should be the translation key
 *
 * Usage in HTML: <span data-i18n="ui.plants">Plants:</span>
 */
export function translatePage(): void {
  const elements = document.querySelectorAll('[data-i18n]')

  elements.forEach((element) => {
    const key = element.getAttribute('data-i18n')
    if (key) {
      element.textContent = t(key)
    }
  })

  // Also update the page title
  document.title = t('app.title')
}
